/**
 * Sistema di fingerprinting robusto e persistente
 * Combina multiple tecniche per massima stabilità
 */

// Funzione per generare un hash stabile
function generateStableHash(data: string): string {
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
}

// Raccoglie informazioni di base del dispositivo
function getBasicDeviceInfo() {
  const nav = navigator;
  const screen = window.screen;
  
  return {
    // Informazioni browser
    userAgent: nav.userAgent,
    language: nav.language,
    languages: nav.languages?.join(',') || '',
    platform: nav.platform,
    cookieEnabled: nav.cookieEnabled,
    doNotTrack: nav.doNotTrack,
    
    // Informazioni schermo
    screenWidth: screen.width,
    screenHeight: screen.height,
    colorDepth: screen.colorDepth,
    pixelDepth: screen.pixelDepth,
    availWidth: screen.availWidth,
    availHeight: screen.availHeight,
    
    // Informazioni timezone
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
    
    // Informazioni hardware
    hardwareConcurrency: nav.hardwareConcurrency || 0,
    maxTouchPoints: nav.maxTouchPoints || 0,
    
    // Informazioni memoria (se disponibili)
    deviceMemory: (nav as any).deviceMemory || 0,
    
    // Informazioni connessione (se disponibili)
    connectionType: (nav as any).connection?.effectiveType || 'unknown',
  };
}

// Genera fingerprint WebGL più stabile
function generateStableWebGLFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as WebGLRenderingContext;
    
    if (!gl) return 'no-webgl';
    
    const webglInfo = [
      gl.getParameter(gl.VERSION),
      gl.getParameter(gl.VENDOR),
      gl.getParameter(gl.RENDERER),
      gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
      gl.getParameter(gl.MAX_TEXTURE_SIZE),
      gl.getParameter(gl.MAX_VIEWPORT_DIMS),
      gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
      gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS),
      gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS),
      gl.getParameter(gl.MAX_VARYING_VECTORS),
      gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS),
      gl.getParameter(gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS),
      gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS),
    ].join('|');
    
    return generateStableHash(webglInfo);
  } catch (error) {
    return 'webgl-error';
  }
}

// Genera fingerprint Canvas più stabile
function generateStableCanvasFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return 'no-canvas';
    
    // Testo base per il fingerprint
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Device fingerprint test', 2, 2);
    
    // Aggiungi forme geometriche
    ctx.fillRect(10, 10, 20, 20);
    ctx.beginPath();
    ctx.arc(50, 50, 20, 0, 2 * Math.PI);
    ctx.fill();
    
    return generateStableHash(canvas.toDataURL());
  } catch (error) {
    return 'canvas-error';
  }
}

// Genera fingerprint Audio più stabile
function generateStableAudioFingerprint(): string {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const analyser = audioContext.createAnalyser();
    const gainNode = audioContext.createGain();
    const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);
    
    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(10000, audioContext.currentTime);
    
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    
    oscillator.connect(analyser);
    analyser.connect(scriptProcessor);
    scriptProcessor.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.start(0);
    
    const fingerprint = [
      audioContext.sampleRate,
      analyser.frequencyBinCount,
      analyser.minDecibels,
      analyser.maxDecibels,
      analyser.smoothingTimeConstant,
    ].join('|');
    
    oscillator.stop();
    audioContext.close();
    
    return generateStableHash(fingerprint);
  } catch (error) {
    return 'audio-error';
  }
}

// Sistema di persistenza robusto
class DeviceFingerprintManager {
  private static instance: DeviceFingerprintManager;
  private deviceId: string | null = null;
  private fallbackId: string | null = null;
  
  private constructor() {
    this.loadStoredId();
  }
  
  static getInstance(): DeviceFingerprintManager {
    if (!DeviceFingerprintManager.instance) {
      DeviceFingerprintManager.instance = new DeviceFingerprintManager();
    }
    return DeviceFingerprintManager.instance;
  }
  
  // Carica ID salvato da storage multipli
  private loadStoredId(): void {
    // Prova localStorage
    this.deviceId = localStorage.getItem('device_id');
    if (this.deviceId) return;
    
    // Prova sessionStorage come fallback
    this.deviceId = sessionStorage.getItem('device_id');
    if (this.deviceId) return;
    
    // Prova IndexedDB come ultimo fallback
    this.loadFromIndexedDB();
  }
  
  // Salva ID in storage multipli
  private saveId(id: string): void {
    this.deviceId = id;
    
    try {
      localStorage.setItem('device_id', id);
    } catch (error) {
      console.warn('localStorage non disponibile, uso sessionStorage');
    }
    
    try {
      sessionStorage.setItem('device_id', id);
    } catch (error) {
      console.warn('sessionStorage non disponibile');
    }
    
    // Salva anche in IndexedDB per persistenza a lungo termine
    this.saveToIndexedDB(id);
  }
  
  // Gestione IndexedDB per persistenza robusta
  private async loadFromIndexedDB(): Promise<void> {
    try {
      const request = indexedDB.open('DeviceFingerprint', 1);
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('fingerprints')) {
          db.createObjectStore('fingerprints', { keyPath: 'id' });
        }
      };
      
      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const transaction = db.transaction(['fingerprints'], 'readonly');
        const store = transaction.objectStore('fingerprints');
        const getRequest = store.get('device_id');
        
        getRequest.onsuccess = () => {
          if (getRequest.result) {
            this.deviceId = getRequest.result.value;
          }
        };
      };
    } catch (error) {
      console.warn('IndexedDB non disponibile');
    }
  }
  
  private async saveToIndexedDB(id: string): Promise<void> {
    try {
      const request = indexedDB.open('DeviceFingerprint', 1);
      
      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const transaction = db.transaction(['fingerprints'], 'readwrite');
        const store = transaction.objectStore('fingerprints');
        store.put({ id: 'device_id', value: id, timestamp: Date.now() });
      };
    } catch (error) {
      console.warn('Errore salvataggio IndexedDB');
    }
  }
  
  // Genera nuovo fingerprint robusto
  async generateRobustFingerprint(): Promise<string> {
    try {
      const basicInfo = getBasicDeviceInfo();
      const webglFingerprint = generateStableWebGLFingerprint();
      const canvasFingerprint = generateStableCanvasFingerprint();
      const audioFingerprint = generateStableAudioFingerprint();
      
      // Combina tutti i fingerprint
      const combinedData = [
        JSON.stringify(basicInfo),
        webglFingerprint,
        canvasFingerprint,
        audioFingerprint,
        Date.now().toString().slice(0, -3), // Timestamp senza millisecondi
      ].join('|||');
      
      const fingerprint = generateStableHash(combinedData);
      
      // Salva il fingerprint
      this.saveId(fingerprint);
      
      return fingerprint;
    } catch (error) {
      console.error('Errore generazione fingerprint:', error);
      return this.getFallbackId();
    }
  }
  
  // Ottiene ID esistente o genera nuovo
  async getDeviceId(): Promise<string> {
    if (this.deviceId) {
      return this.deviceId;
    }
    
    return await this.generateRobustFingerprint();
  }
  
  // ID di fallback basato su caratteristiche di base
  private getFallbackId(): string {
    if (this.fallbackId) {
      return this.fallbackId;
    }
    
    const basicInfo = getBasicDeviceInfo();
    this.fallbackId = generateStableHash(JSON.stringify(basicInfo));
    return this.fallbackId;
  }
  
  // Forza rigenerazione del fingerprint
  async regenerateFingerprint(): Promise<string> {
    this.deviceId = null;
    this.fallbackId = null;
    return await this.generateRobustFingerprint();
  }
  
  // Verifica se il fingerprint è stabile
  async verifyFingerprintStability(): Promise<boolean> {
    const currentId = await this.getDeviceId();
    const newId = await this.generateRobustFingerprint();
    
    return currentId === newId;
  }
}

// Esporta funzioni principali
export const deviceFingerprintManager = DeviceFingerprintManager.getInstance();

export async function generateRobustDeviceHash(): Promise<string> {
  return await deviceFingerprintManager.getDeviceId();
}

export async function generateRobustDeviceName(): Promise<string> {
  const basicInfo = getBasicDeviceInfo();
  const platform = basicInfo.platform || 'unknown';
  const browser = basicInfo.userAgent.includes('Chrome') ? 'Chrome' : 
                  basicInfo.userAgent.includes('Firefox') ? 'Firefox' : 
                  basicInfo.userAgent.includes('Safari') ? 'Safari' : 'Unknown';
  
  return `${platform} ${browser} (${basicInfo.screenWidth}x${basicInfo.screenHeight})`;
}

export function getRobustDevicePlatform(): string {
  const basicInfo = getBasicDeviceInfo();
  return basicInfo.platform || 'unknown';
}

export async function regenerateDeviceFingerprint(): Promise<string> {
  return await deviceFingerprintManager.regenerateFingerprint();
}

export async function verifyDeviceStability(): Promise<boolean> {
  return await deviceFingerprintManager.verifyFingerprintStability();
}
